import React from 'react';
import { ShieldCheck, Globe, Zap, BadgeDollarSign, FileCheck, Lock } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import MobileSlider from './MobileSlider';

const benefits = [
  { icon: ShieldCheck, title: "Federal Ownership", description: "A registered mark establishes exclusive nationwide legal ownership. No competitor can use your name, logo, or slogan." },
  { icon: Globe, title: "50-State Coverage", description: "One federal filing protects your brand in every US state — a single registration for coast-to-coast exclusivity." },
  { icon: Zap, title: "Enforcement Power", description: "Registered marks carry priority in litigation, access to statutory damages, and the authority to shut down infringers." },
  { icon: BadgeDollarSign, title: "Increased Valuation", description: "Registered intellectual property is a tangible asset. Investors and acquirers value businesses with strong IP portfolios." },
  { icon: FileCheck, title: "Platform Eligibility", description: "Amazon Brand Registry, Meta Business verification, and premium seller tools all require a registered or pending trademark." },
  { icon: Lock, title: "Perpetual Protection", description: "Once registered, your trademark can be renewed indefinitely. A single filing today provides permanent brand security." }
];

const BenefitCard = ({ benefit, index }) => {
  const card = useScrollAnimation({ threshold: 0.1 });
  return (
    <div ref={card.ref} data-testid={`benefit-item-${index}`}
      className={`group relative border border-[#E2E2F0] bg-white rounded-2xl p-7 overflow-hidden cursor-default h-full
        transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(99,102,241,0.2)] hover:border-[#C7D2FE]
        scroll-fade-up scroll-delay-${(index%3)+1} ${card.isVisible?'scroll-visible':''}`}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
      <div className="relative z-10">
        <div className="w-12 h-12 rounded-xl bg-[#F5F3FF] border border-[#E2E2F0] flex items-center justify-center mb-5
          group-hover:bg-white/20 group-hover:border-white/30 transition-all duration-500">
          <benefit.icon className="w-5 h-5 text-[#6366F1] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
        </div>
        <h3 className="text-lg font-semibold tracking-tight text-[#1E1B4B] group-hover:text-white mb-2 transition-colors duration-500" style={{fontFamily:"'Playfair Display',serif"}}>{benefit.title}</h3>
        <p className="text-sm font-normal text-[#4B5563] group-hover:text-white/80 leading-relaxed transition-colors duration-500">{benefit.description}</p>
      </div>
    </div>
  );
};

const BenefitsSection = () => {
  const header = useScrollAnimation({ threshold: 0.2 });
  return (
    <section id="benefits" data-testid="benefits-section" className="py-16 md:py-32 bg-[#F5F3FF]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={header.ref} className={`mb-10 md:mb-16 scroll-fade-up ${header.isVisible?'scroll-visible':''}`}>
          <span className="text-xs tracking-[0.2em] uppercase text-[#6366F1] mb-3 block font-semibold">Advantages</span>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1E1B4B] mb-4" style={{fontFamily:"'Playfair Display',serif"}}>What Federal Registration Delivers.</h2>
          <p className="text-base font-normal text-[#4B5563] leading-relaxed max-w-lg">Trademark registration is the legal foundation that enables your brand to grow, compete, and defend itself.</p>
        </div>
        <div className="hidden md:grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {benefits.map((b,i)=><BenefitCard key={i} benefit={b} index={i} />)}
        </div>
        <div className="md:hidden">
          <MobileSlider>{benefits.map((b,i)=><BenefitCard key={i} benefit={b} index={i} />)}</MobileSlider>
        </div>
      </div>
    </section>
  );
};
export default BenefitsSection;
